import React from 'react';
import {
  Box,
  Container,
  Typography,
  Breadcrumbs,
  Link,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

const PrivacyPolicyPage: React.FC = () => {
  const theme = useTheme();

  const sectionHeaderSx = {
    color: theme.palette.text.primary,
    mt: 5,
    mb: 2,
    fontWeight: 600,
  };

  const bodySx = {
    color: theme.palette.text.secondary,
    mb: 2,
    lineHeight: 1.8,
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: theme.palette.background.default,
        py: 6,
      }}
    >
      <Container maxWidth="md">
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link component={RouterLink} to="/" color="inherit" underline="hover">
            Home
          </Link>
          <Typography color="text.primary">Privacy Policy</Typography>
        </Breadcrumbs>

        <Typography variant="h3" sx={{ color: theme.palette.text.primary, mb: 1, fontWeight: 700 }}>
          Privacy Policy
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 4 }}>
          Last updated: March 2026
        </Typography>

        <Divider sx={{ mb: 4 }} />

        {/* 1. Data Controller */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          1. Data Controller
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          The data controller responsible for processing your personal data is:
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          <strong>AmudFin S.A.</strong><br />
          Registered office: 9, Boulevard Prince Henri, L-1724 Luxembourg<br />
          Email: privacy@amudfin.com<br />
          Data Protection Officer (DPO): dpo@amudfin.com
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          This Privacy Policy is issued pursuant to Articles 13 and 14 of Regulation (EU) 2016/679
          (the General Data Protection Regulation, or &ldquo;GDPR&rdquo;) and the Luxembourg Law of 1 August 2018
          on the organisation of the Commission Nationale pour la Protection des Données (CNPD).
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* 2. Categories of Personal Data */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          2. Categories of Personal Data Collected
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          We collect and process the following categories of personal data depending on your use of
          our platform:
        </Typography>

        <TableContainer component={Paper} sx={{ mb: 3, bgcolor: theme.palette.background.paper }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Examples</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Account Data</TableCell>
                <TableCell>Full name, email address, job title, organisation name, phone number, password hash</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Financial Data</TableCell>
                <TableCell>Fund positions, NAV records, transaction history, investor register entries, AML screening results</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Usage Data</TableCell>
                <TableCell>Pages visited, features used, session duration, actions performed (audit trail)</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Device &amp; Technical Data</TableCell>
                <TableCell>IP address, browser type and version, operating system, device identifiers, time zone</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Authentication Data</TableCell>
                <TableCell>Two-factor authentication secrets (encrypted), login timestamps, session tokens</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ my: 3 }} />

        {/* 3. Legal Basis */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          3. Legal Basis for Processing
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          We process your personal data on the following legal bases under Article 6(1) GDPR:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText
              primary="Performance of a contract (Art. 6(1)(b))"
              secondary="Processing necessary to provide the AmudFin platform services you have subscribed to, including account management, fund operations, NAV calculations, and transaction processing."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Compliance with a legal obligation (Art. 6(1)(c))"
              secondary="Processing required under Luxembourg financial regulation, including CSSF reporting obligations, AML/KYC requirements under the Law of 12 November 2004 (as amended), UCITS Directive (2009/65/EC), and tax reporting obligations."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Legitimate interests (Art. 6(1)(f))"
              secondary="Processing necessary for platform security, fraud prevention, service improvement, and analytics. Our legitimate interest is balanced against your rights and does not override your fundamental freedoms."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Consent (Art. 6(1)(a))"
              secondary="Where required, such as for optional analytics cookies or marketing communications. You may withdraw consent at any time without affecting the lawfulness of processing prior to withdrawal."
            />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        {/* 4. Purposes of Processing */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          4. Purposes of Processing
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          Your personal data is processed for the following purposes:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="Account creation, authentication, and access management (including two-factor authentication)" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Fund management operations: NAV calculations, share class management, investor register maintenance, and transaction processing" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Regulatory compliance: AML/KYC screening, CSSF reporting, UCITS compliance monitoring, and audit trail maintenance" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Four-eyes approval workflows and segregation of duties enforcement" />
          </ListItem>
          <ListItem>
            <ListItemText primary="AI-assisted compliance scoring and predictive regulatory analytics" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Platform security: intrusion detection, session management, and access logging" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Service improvement through anonymised usage analytics" />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        {/* 5. Data Recipients and Transfers */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          5. Data Recipients and International Transfers
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          Your personal data may be shared with the following categories of recipients, all of whom
          are bound by appropriate data processing agreements:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText
              primary="Infrastructure sub-processors"
              secondary="Cloud hosting and database services located exclusively within the European Union (EU/EEA). No personal data is transferred outside the EU/EEA."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Regulatory authorities"
              secondary="The Commission de Surveillance du Secteur Financier (CSSF), CNPD, or other competent authorities where disclosure is required by law."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Your organisation"
              secondary="Administrators within your subscribing organisation who have legitimate access to platform data as part of their fund management responsibilities."
            />
          </ListItem>
        </List>
        <Typography variant="body1" sx={bodySx}>
          We do not sell personal data to third parties. We do not share personal data with
          advertising networks or data brokers.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* 6. Retention Periods */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          6. Data Retention Periods
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          We retain personal data only as long as necessary for the purposes for which it was
          collected, or as required by applicable law. The following retention periods apply in
          accordance with Luxembourg regulatory requirements:
        </Typography>

        <TableContainer component={Paper} sx={{ mb: 3, bgcolor: theme.palette.background.paper }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Data Category</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Retention Period</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Legal Basis</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>AML/KYC records</TableCell>
                <TableCell>10 years after business relationship ends</TableCell>
                <TableCell>Law of 12 November 2004 (AML), CSSF Regulation 12-02</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Transaction records</TableCell>
                <TableCell>7 years</TableCell>
                <TableCell>Luxembourg Commercial Code (Art. 16), Tax Code</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>MiFID II records</TableCell>
                <TableCell>5 years (7 years where CSSF requests extension)</TableCell>
                <TableCell>MiFID II Directive 2014/65/EU, CSSF Circular 07/307</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Audit logs</TableCell>
                <TableCell>5 years</TableCell>
                <TableCell>CSSF Circular 17/654 (IT governance)</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Account data</TableCell>
                <TableCell>Duration of contract + 3 years</TableCell>
                <TableCell>Contractual necessity and limitation period</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Usage analytics</TableCell>
                <TableCell>26 months (anonymised)</TableCell>
                <TableCell>Legitimate interest</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ my: 3 }} />

        {/* 7. Data Subject Rights */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          7. Your Rights Under GDPR
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          Under the GDPR, you have the following rights with respect to your personal data. To
          exercise any of these rights, contact us at privacy@amudfin.com. We will respond within
          one month of receiving your request, as required by Article 12(3) GDPR.
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText
              primary="Right of access (Art. 15)"
              secondary="You may request a copy of the personal data we hold about you, together with information about how it is processed."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Right to rectification (Art. 16)"
              secondary="You may request correction of inaccurate personal data or completion of incomplete data."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Right to erasure (Art. 17)"
              secondary="You may request deletion of your personal data where it is no longer necessary for the purposes collected, subject to legal retention obligations."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Right to restriction of processing (Art. 18)"
              secondary="You may request that we restrict processing of your data in certain circumstances, such as while we verify accuracy of contested data."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Right to data portability (Art. 20)"
              secondary="You may request to receive your personal data in a structured, commonly used, machine-readable format (JSON or CSV)."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Right to object (Art. 21)"
              secondary="You may object to processing based on legitimate interests. We will cease processing unless we demonstrate compelling legitimate grounds."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Right not to be subject to automated decision-making (Art. 22)"
              secondary="See Section 8 below regarding our AI compliance scoring system."
            />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        {/* 8. Automated Decision-Making */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          8. Automated Decision-Making and Profiling
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          AmudFin uses AI-assisted compliance scoring to help fund administrators assess regulatory
          risk. This system analyses fund data, transaction patterns, and regulatory filings to
          generate compliance risk scores.
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          These scores are used as a decision-support tool and do not produce legal effects or
          similarly significant effects on individuals without human review. All compliance
          decisions are subject to the four-eyes approval workflow, ensuring that no automated
          score alone determines a regulatory outcome.
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          You have the right to request human intervention, express your point of view, and contest
          any decision influenced by automated processing, in accordance with Article 22 GDPR.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* 9. Security Measures */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          9. Security of Processing
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          In accordance with Article 32 GDPR, we implement appropriate technical and organisational
          measures to ensure a level of security appropriate to the risk, including:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="Encryption of personal data in transit (TLS 1.2+) and at rest (AES-256)" />
          </ListItem>
          <ListItem>
            <ListItemText primary="RS256 JWT-based authentication with mandatory two-factor authentication for administrative accounts" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Role-based access control with four-eyes segregation of duties" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Comprehensive audit logging of all data access and modifications" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Regular security assessments and penetration testing" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Data processing exclusively within EU/EEA-based infrastructure" />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        {/* 10. Supervisory Authority */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          10. Right to Lodge a Complaint
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          If you believe that our processing of your personal data infringes the GDPR, you have the
          right to lodge a complaint with the competent supervisory authority. For Luxembourg, the
          supervisory authority is:
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          <strong>Commission Nationale pour la Protection des Données (CNPD)</strong><br />
          15, Boulevard du Jazz, L-4370 Belvaux, Luxembourg<br />
          Tel: (+352) 26 10 60 -1<br />
          Website: www.cnpd.public.lu<br />
          Email: info@cnpd.lu
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          We encourage you to contact our DPO at dpo@amudfin.com before filing a complaint, so we
          may address your concerns directly.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* 11. Changes */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          11. Changes to This Privacy Policy
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          We may update this Privacy Policy from time to time to reflect changes in our processing
          activities or applicable law. Material changes will be communicated to you via email or
          in-platform notification at least 30 days before they take effect. The &ldquo;Last
          updated&rdquo; date at the top of this page indicates the most recent revision.
        </Typography>

        <Box sx={{ mt: 6, mb: 4 }}>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            For questions about this Privacy Policy or to exercise your data subject rights, contact
            us at privacy@amudfin.com or write to our Data Protection Officer at dpo@amudfin.com.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default PrivacyPolicyPage;
